/**
 * SideRails - the actor and the target as narrow rails beside the display
 * (plan map-display, Track E; the session study). Each rail is a summary -
 * who, how hurt, what is in hand or how they stand - and the whole rail is
 * the one click to its full panel, which opens over the map's edge and keeps
 * everything the panel always had (it stays mounted: its refs are read once).
 */
import { liveryByCombatant } from '../../engine/roster/team';
import LinkedChevrons from '../killTeam/LinkedChevrons';
import { inSquadMode } from '../../engine/killTeam/marks';
import { useAttacker } from '../../contexts/AttackerContext';
import { useTargets } from '../../contexts/TargetsContext';
import { useRosterContext } from '../../contexts/RosterContext';
import { useCombatFlow } from '../../contexts/CombatFlowContext';
import { useSession } from '../../contexts/SessionContext';
import { useCallback, useMemo, useRef, useState } from 'react';
import { RANGE_BAND_LABELS } from '../../engine/attackCalc';
import { healthBandLabel, healthColor, vitalsOf } from '../../engine/healthRamp';
import { resolveReactionAvailability } from '../../engine/reactionAvailability';
import { RUNG_LABELS } from '../../engine/stealth/ladder';
import { resolvePlayersSeeMuster, resolveReactionOptions } from '../../engine/settings/resolve';
import { getBodyConfig, resolveArmourAP } from '../../engine/bodyConfig';
import { effectiveVehicleFacing } from '../../engine/vehicleFacing';
import { useWoundDisplay } from '../../hooks/useWoundDisplay';
import ConditionIcon from '../ConditionIcon';
import SquadBar from '../SquadBar';
import MusterBar from '../MusterBar';
import useMusterFit from '../../hooks/useMusterFit';
import { useSeesHidden } from '../../hooks/useSeesHidden';
import { fitKey, musterOf } from '../../engine/roster/muster';
import { COVER_SHORT } from '../StatusBand';
import { rangeFact } from '../TargetIdentityHeader';
import styles from '../../styles/SideRails.module.css';
import { countOf } from '../../utils/countOf';

/**
 * A wounds bar on the app's own health ramp (engine/healthRamp), and the numbers - never "down": at 0 they
 * still stand - or, on a table playing without numbers, the band's name (useWoundDisplay). A horde reads its
 * magnitude.
 */
function Wounds({ target, barOnly = false }) {
  const { showNumbers } = useWoundDisplay();
  if (!target?.wounds) return null;
  const { shown, total } = vitalsOf(target);
  const left = total ? Math.max(0, shown) / total : 0;
  return (
    <>
      <span className={styles.bar}>
        <i style={{ width: `${Math.round(left * 100)}%`, background: healthColor(shown, total) }} />
      </span>
      {!barOnly && (
        <span className={styles.num}>{showNumbers ? `${shown} / ${total}` : healthBandLabel(shown, total)}</span>
      )}
    </>
  );
}

/** The conditions as the markers draw them (ConditionIcon); past six, how many more. */
function Glyphs({ conditions }) {
  if (!conditions?.length) return null;
  return (
    <span className={styles.glyphs}>
      {conditions.slice(0, 6).map((code) => (
        <ConditionIcon key={code} code={code} size={13} />
      ))}
      {conditions.length > 6 && <span className={styles.num}>+{conditions.length - 6}</span>}
    </span>
  );
}

/**
 * THE REACTION (plan design-gaps, L7; the session page's rails): OWED while the reaction queue waits on this
 * combatant, else what they could take - DODGE, PARRY, both, or NONE - under the table's condition rules
 * (`rules`, as the defence prompt reads them). The app keeps no "spent this round"; what it knows is who owes and
 * who can.
 */
function Reaction({ target, owed, rules }) {
  if (!target) return null;
  const words = () => {
    const { canDodge, canParry } = resolveReactionAvailability(target, rules);
    return [canDodge && 'DODGE', canParry && 'PARRY'].filter(Boolean).join(' · ') || 'NO REACTION';
  };
  return (
    <span className={owed ? styles.owed : styles.word} data-testid="rail-reaction" data-owed={owed || undefined}>
      {owed ? 'OWED' : words()}
    </span>
  );
}

/** The table's reaction rules off the session, memoised on the permissions document. */
function useReactionRules() {
  const { permissions } = useSession();
  return useMemo(() => resolveReactionOptions(permissions), [permissions]);
}

/**
 * The armour where you aim: the called location's under a called shot, else the body's, read the way the engine
 * resolves a hit (`resolveArmourAP` with the EFFECTIVE facing: a facing body answers with its facing, a mapped one
 * with its key).
 */
function Armour({ target, location, called }) {
  if (!target?.armour) return null;
  const at = called && location ? location : 'body';
  const ap = resolveArmourAP(target.armour, at, getBodyConfig(target.bodyType), effectiveVehicleFacing(target));
  return (
    <span className={styles.cap} data-testid="rail-armour" data-location={at}>
      {called ? `${at} ${ap}` : `ARMOUR ${ap}`}
    </span>
  );
}

/**
 * The range as the identity header reads it: the map's metres with the MAP's band (never the chosen one beside a
 * measured distance, which contradicts itself), or the band in force alone when the map has no answer.
 */
function Range({ metres, bandFromMap, band }) {
  const text = metres != null ? rangeFact(metres, bandFromMap) : band ? RANGE_BAND_LABELS[band] || band : null;
  if (!text) return null;
  return (
    <span className={styles.cap} data-testid="rail-range">
      {text}
    </span>
  );
}

/** "COVER HALF · AP 4": the level the map derives (the status band's word for it) and the armour the cover lends. */
function CoverLine({ cover }) {
  if (!cover || cover.level === 'none') return null;
  return (
    <span className={styles.cap} data-testid="rail-cover">
      {`COVER ${COVER_SHORT[cover.level] || cover.level} · AP ${cover.ap}`}
    </span>
  );
}

/** Each side's words: the foot's label, the panel it opens, the name when there is nobody. */
const SIDE = {
  actor: { label: 'ACTOR ▸', panel: 'Actor', empty: 'NOBODY' },
  target: { label: '◂ TARGET', panel: 'Target', empty: 'NO TARGET' },
};

/**
 * The target rail's words by the tab in front of its panel (2026-10-22, plan retire-classic): a click opens the
 * Story or the Stealth panel in those modes, and the rail said TARGET. The panel itself keeps its name ("Target
 * panel"): it holds all three tabs.
 */
const FRONT_WORDS = {
  story: { ...SIDE.target, label: '◂ STORY', panel: 'Story' },
  stealth: { ...SIDE.target, label: '◂ STEALTH', panel: 'Stealth' },
  target: SIDE.target,
};

/**
 * What waits behind the front tab, in one line, or null: Story's open calls and clocks ("1 CALL · 3 CLOCKS"),
 * Stealth's rung (the strip's word, in its colour). The target's own line is its reaction, as it always was.
 */
export function frontWaiting({ front, calls, clocks, rung }) {
  // A rung this build does not know (a newer document) says nothing rather than breaking the rail.
  if (front === 'stealth') return RUNG_LABELS[rung] ? { text: RUNG_LABELS[rung].toUpperCase(), rung } : null;
  if (front !== 'story') return null;
  const parts = [calls > 0 && countOf(calls, 'CALL', 'CALLS'), clocks > 0 && countOf(clocks, 'CLOCK', 'CLOCKS')].filter(
    Boolean,
  );
  return parts.length ? { text: parts.join(' · ') } : null;
}

/** One rail: the avatar, the name, what follows, and the foot that says where a click goes. */
function Rail({ side, name, open, onToggle, slim = false, words = SIDE[side], children }) {
  return (
    <button
      type="button"
      className={styles.rail}
      data-side={side}
      data-slim={slim || undefined}
      data-testid={`rail-${side}`}
      aria-expanded={open}
      title={name ? `${name}: open the ${words.panel} panel` : undefined}
      onClick={onToggle}
    >
      <span className={styles.avatar} data-side={side}>
        {name ? name.charAt(0) : '?'}
      </span>
      <span className={styles.name}>{name ?? words.empty}</span>
      {children}
      <span className={styles.open}>{words.label}</span>
    </button>
  );
}

/**
 * The muster's data and its fit: the blocks, the step it draws at, the two elements the fit measures, and whether
 * the acted are shown at the fourth step (their stub opened - a change of structure, so the fit starts over).
 * Returns the column's ref and step, and the bar's own props.
 */
function useMusterColumn(railRows, seesHidden) {
  const { roster, actorId } = useRosterContext();
  const { reactionOwedIds } = useCombatFlow();
  // The owed list is a new array every shell render: held by its content, or the muster's memo never holds.
  const owedKey = reactionOwedIds.join('|');
  const owedIds = useMemo(() => (owedKey ? owedKey.split('|') : EMPTY_ROWS), [owedKey]);
  const muster = useMemo(
    () => musterOf({ rows: railRows ?? EMPTY_ROWS, roster, actorId: actorId ?? null, owedIds, viewerIsGm: seesHidden }),
    [railRows, roster, actorId, owedIds, seesHidden],
  );
  const listRef = useRef(null);
  const columnRef = useRef(null);
  const [actedOpen, setActedOpen] = useState(false);
  const key = `${fitKey(muster)}${actedOpen ? '+acted' : ''}`;
  const step = useMusterFit({ listRef, columnRef, key, max: MUSTER_MAX_STEP });
  const onToggleActed = useCallback(() => setActedOpen((o) => !o), []);
  return { columnRef, step, bar: { muster, step, listRef, actedOpen, onToggleActed } };
}

const EMPTY_ROWS = [];

/** The last density step the muster may take (utils/musterFit): the acted folded and the column scrolling. */
const MUSTER_MAX_STEP = 4;

/** The rail's summary: everything, or - slim, at the muster's third step - the wounds bar alone. */
function ActorSummary({ slim }) {
  const atk = useAttacker();
  const { actorId, roster } = useRosterContext();
  const actorLivery = useMemo(() => (actorId ? liveryByCombatant(roster)[actorId] : null), [roster, actorId]);
  const { reactionOwedIds } = useCombatFlow();
  const rules = useReactionRules();
  const me = atk.actorTarget;
  const slot = atk.slot;
  if (slim) return <Wounds target={me} barOnly />;
  return (
    <>
      <Wounds target={me} />
      {slot && !slot.isEmpty && <span className={styles.cap}>{slot.weaponName}</span>}
      {slot && slot.weaponType !== 'melee' && slot.ammo?.max > 0 && (
        <span className={styles.num}>{`${slot.ammo.current} / ${slot.ammo.max}`}</span>
      )}
      {atk.fireMode && <span className={styles.word}>{String(atk.fireMode).toUpperCase()}</span>}
      <Reaction target={me} owed={!!actorId && reactionOwedIds.includes(actorId)} rules={rules} />
      {inSquadMode(me) && <LinkedChevrons livery={actorLivery} />}
      <Glyphs conditions={me?.conditions} />
    </>
  );
}

/** For a player without the muster: the squad's chips, stacked (plan design-gaps, L7). */
function SquadRail() {
  const { roster, actorId, setActorId } = useRosterContext();
  const { reactionOwedIds } = useCombatFlow();
  return (
    <SquadBar
      roster={roster}
      actorId={actorId}
      onSelect={setActorId}
      owedIds={reactionOwedIds}
      compact
      testId="squad-rail"
    />
  );
}

/**
 * The actor's column: the rail (wounds, the weapon and its clip, the fire mode, the reaction, the conditions) and
 * under it the MUSTER (2026-10-22, plan muster) - everyone on the stage, one click each - for the GM, solo play, and
 * players when the table allows it; otherwise the squad's chips. As the muster grows the rail slims (its fit's
 * third step) to give it room.
 */
export function ActorRail({ open, onToggle, railRows }) {
  const atk = useAttacker();
  const { setActorId } = useRosterContext();
  const { permissions } = useSession();
  const seesHidden = useSeesHidden();
  // The GM and solo play always; a player when the table says so (default off).
  const showMuster = seesHidden || resolvePlayersSeeMuster(permissions);
  // Hooks run either way; a screen without the muster gives it no rows, so it builds nothing (the review).
  const { columnRef, step, bar } = useMusterColumn(showMuster ? railRows : null, seesHidden);
  const slim = showMuster && step >= 3;
  return (
    <div ref={columnRef} className={styles.column} data-side="actor" data-muster={showMuster || undefined}>
      <Rail side="actor" name={atk.actorTarget?.name} open={open} onToggle={onToggle} slim={slim}>
        <ActorSummary slim={slim} />
      </Rail>
      {showMuster ? <MusterBar {...bar} onSelect={setActorId} /> : <SquadRail />}
    </div>
  );
}

/** The target's rail: wounds, the range in metres and its band, the cover, the armour where you aim, the reaction, the conditions. */
/** The panel's front as the shell reads it (`columnsProps.targetFront`): the target, nothing waiting, by default. */
const TARGET_FRONT = { front: 'target', calls: 0, clocks: 0, rung: null };

export function TargetRail({ open, onToggle, front = TARGET_FRONT }) {
  const { currentTab, activeTab } = useTargets();
  const { reactionOwedIds } = useCombatFlow();
  const rules = useReactionRules();
  const atk = useAttacker();
  const target = currentTab?.target;
  const waiting = frontWaiting(front);
  return (
    <Rail side="target" name={target?.name} open={open} onToggle={onToggle} words={FRONT_WORDS[front.front]}>
      <Wounds target={target} />
      {target && <Range metres={atk.mapMetres} bandFromMap={atk.rangeBandFromMap} band={atk.rangeBand} />}
      <CoverLine cover={target?.cover} />
      <Armour target={target} location={atk.targetLocation} called={!!atk.attackToggles?.calledShot} />
      <Reaction target={target} owed={!!activeTab && reactionOwedIds.includes(activeTab)} rules={rules} />
      <Glyphs conditions={target?.conditions} />
      {waiting && (
        <span className={styles.waiting} data-testid="rail-front-waiting" data-rung={waiting.rung}>
          {waiting.text}
        </span>
      )}
    </Rail>
  );
}
